import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { estimatesAPI, purchasesAPI, settingsAPI } from '../../api';
import { clientsAPI } from '../../api';
import ClientPicker from '../../components/ClientPicker';
import Layout from '../../components/Layout';
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext';
import { X, Building2, User, Eye, Pencil, Plus, Mail, Phone, Printer, RefreshCw } from 'lucide-react';
import SlideOver from '../../components/ui/SlideOver';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Textarea } from '../../components/ui/Textarea';
import { MinimalRichTextEditor } from '../../components/ui/MinimalRichTextEditor';
import { Card, CardContent } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import {
  labelPortalRejectTags,
  portalFeedbackActionLabel,
  PORTAL_QUERY_REASON_LABELS,
} from '../../lib/estimateClientPortalFeedback';

function resolveDefaultInvoiceVatRate(invoicePrefs) {
  const raw = invoicePrefs?.default_vat_rate;
  if (raw === 0) return 0;
  const n = parseFloat(raw);
  return Number.isFinite(n) && n >= 0 ? n : 20;
}

/** Add calendar days to a yyyy-MM-dd string (local date; avoids UTC shift). */
function addDaysToYyyyMmDd(ymd, days) {
  if (!ymd || !/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  const [y, m, d] = ymd.split('-').map((n) => parseInt(n, 10));
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return '';
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  const yy = dt.getFullYear();
  const mm = String(dt.getMonth() + 1).padStart(2, '0');
  const dd = String(dt.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
}

const EstimateEditor = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;
  const formRef = useRef(null);
  const { registerCheck, unregisterCheck } = useUnsavedChanges();
  const hasUnsavedChangesRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const fetchingRef = useRef(false);
  const [estimateStatus, setEstimateStatus] = useState(null);
  const [clientPortalFeedback, setClientPortalFeedback] = useState(null);
  const [clientPortalFeedbackPending, setClientPortalFeedbackPending] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [converting, setConverting] = useState(false);

  // Estimate form state
  const [formData, setFormData] = useState({
    clientId: '',
    estimateNum: '',
    reference: '',
    dateCreated: new Date().toISOString().split('T')[0],
    dateIssued: '',
    expiryDate: '',
    vatRate: 20,
    notes: '',
    terms: '',
    privateNotes: '',
  });

  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientPicker, setShowClientPicker] = useState(false);
  const [showNewClientSlideOver, setShowNewClientSlideOver] = useState(false);
  const [activeInfoTab, setActiveInfoTab] = useState('notes'); // 'notes' | 'terms' | 'privateNotes'
  
  // Client search/combobox state (for create mode)
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientSearchResults, setClientSearchResults] = useState([]);
  const [clientSearchLoading, setClientSearchLoading] = useState(false);
  const [clientSearchSelectedIndex, setClientSearchSelectedIndex] = useState(-1);
  const [showClientSearchDropdown, setShowClientSearchDropdown] = useState(false);
  const clientSearchTimeoutRef = useRef(null);
  const clientSearchContainerRef = useRef(null);
  const [materials, setMaterials] = useState([]);
  const [labour, setLabour] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });

  // Track initial form state for unsaved changes detection
  const initialFormStateRef = useRef(null);
  const initialMaterialsRef = useRef(null);
  const initialLabourRef = useRef(null);
  const initialClientRef = useRef(null);

  // Material autocomplete state per material row
  const [materialAutocomplete, setMaterialAutocomplete] = useState({});
  const [materialSuggestCoords, setMaterialSuggestCoords] = useState(null);
  const materialDescInputRefs = useRef({});
  const autocompleteTimeoutRef = useRef({});

  // Default values from settings
  const [defaultEstimateNotes, setDefaultEstimateNotes] = useState('');
  const [defaultEstimateTerms, setDefaultEstimateTerms] = useState('');

  // Helper function to convert ISO date string to yyyy-MM-dd format for date inputs
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    // If it's already in yyyy-MM-dd format, return as is
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) return dateString;
    // If it's an ISO datetime string, extract just the date part
    if (dateString.includes('T')) return dateString.split('T')[0];
    // Otherwise try to parse and format
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toISOString().split('T')[0];
    } catch {
      return '';
    }
  };

  const fetchEstimate = useCallback(async () => {
    if (!id || fetchingRef.current) return;
    
    fetchingRef.current = true;
    try {
      setLoading(true);
      setError('');
      setSaveSuccess(false);
      const estimate = await estimatesAPI.getOne(id);

      setFormData({
        clientId: estimate.client.id,
        estimateNum: estimate.estimateNum,
        reference: estimate.reference || '',
        dateCreated: formatDateForInput(estimate.dateCreated) || new Date().toISOString().split('T')[0],
        dateIssued: formatDateForInput(estimate.dateIssued) || '',
        expiryDate: formatDateForInput(estimate.expiryDate) || '',
        vatRate: estimate.vatRate || 20,
        notes: estimate.notes || '',
        terms: estimate.terms || '',
        privateNotes: estimate.privateNotes || '',
      });

      setEstimateStatus(estimate.status);
      setClientPortalFeedback(estimate.clientPortalResponse || null);
      setClientPortalFeedbackPending(!!estimate.clientPortalResponsePending);
      setSelectedClient(estimate.client);
      setMaterials(estimate.materials || []);
      setLabour(estimate.labour || []);

      // Store initial state for comparison
      initialFormStateRef.current = {
        clientId: estimate.client.id,
        estimateNum: estimate.estimateNum,
        reference: estimate.reference || '',
        dateCreated: formatDateForInput(estimate.dateCreated) || new Date().toISOString().split('T')[0],
        dateIssued: formatDateForInput(estimate.dateIssued) || '',
        expiryDate: formatDateForInput(estimate.expiryDate) || '',
        vatRate: estimate.vatRate || 20,
        notes: estimate.notes || '',
        terms: estimate.terms || '',
        privateNotes: estimate.privateNotes || '',
      };
      initialMaterialsRef.current = JSON.parse(JSON.stringify(estimate.materials || []));
      initialLabourRef.current = JSON.parse(JSON.stringify(estimate.labour || []));
      initialClientRef.current = estimate.client;
    } catch (err) {
      if (err.response?.status === 429) {
        setError('Too many requests. Please wait a moment and try again.');
      } else {
        setError(err.message || 'Failed to load estimate');
      }
      console.error('Error fetching estimate:', err);
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  }, [id]);

  useEffect(() => {
    if (!id || !isEditMode || !clientPortalFeedbackPending) return;
    let cancelled = false;
    (async () => {
      try {
        await estimatesAPI.acknowledgeClientResponse(id);
        if (!cancelled) setClientPortalFeedbackPending(false);
      } catch (e) {
        console.error(e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, isEditMode, clientPortalFeedbackPending]);

  useEffect(() => {
    let isMounted = true;

    // Reset client search state when switching modes
    if (!isEditMode) {
      setClientSearchQuery('');
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
      setClientSearchSelectedIndex(-1);
    }

    // Load settings for both edit and create modes (for the "Load default" links)
    const loadSettings = async () => {
      try {
        const settingsResponse = await settingsAPI.get();
        const defaultTerms = settingsResponse?.settings?.preferences?.estimate?.default_estimate_terms || 
                            settingsResponse?.settings?.preferences?.invoice?.default_invoice_terms || '';
        const defaultNotes = settingsResponse?.settings?.preferences?.estimate?.default_estimate_notes || 
                           settingsResponse?.settings?.preferences?.invoice?.default_invoice_notes || '';
        if (isMounted) {
          setDefaultEstimateTerms(defaultTerms);
          setDefaultEstimateNotes(defaultNotes);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
      }
    };
    
    loadSettings();

    if (isEditMode && id && !fetchingRef.current) {
      fetchEstimate();
    } else if (!isEditMode) {
      const initializeNewEstimate = async () => {
        try {
          const [numResponse, settingsResponse] = await Promise.all([
            estimatesAPI.getNextNumber(),
            settingsAPI.get().catch(() => ({ settings: { preferences: {} } })),
          ]);
          const defaultTerms =
            settingsResponse?.settings?.preferences?.estimate?.default_estimate_terms ||
            settingsResponse?.settings?.preferences?.invoice?.default_invoice_terms ||
            '';
          const defaultNotes =
            settingsResponse?.settings?.preferences?.estimate?.default_estimate_notes ||
            settingsResponse?.settings?.preferences?.invoice?.default_invoice_notes ||
            '';
          const defaultVat = resolveDefaultInvoiceVatRate(settingsResponse?.settings?.preferences?.invoice);
          const today = new Date().toISOString().split('T')[0];
          const estimateNum = numResponse?.estimateNum || `EST-${Date.now().toString().slice(-6)}`;

          if (isMounted) {
            setDefaultEstimateTerms(defaultTerms);
            setDefaultEstimateNotes(defaultNotes);
          }

          const newFormData = {
            clientId: '',
            estimateNum,
            reference: '',
            dateCreated: today,
            dateIssued: '',
            expiryDate: '',
            vatRate: defaultVat,
            notes: '',
            terms: defaultTerms,
            privateNotes: '',
          };

          const blankMaterial = {
            id: `temp-${Date.now()}`,
            description: '',
            quantity: 1,
            unitPrice: 0,
            lineTotal: 0,
            partNumber: '',
            notes: '',
          };
          const blankLabour = {
            id: `temp-${Date.now() + 1}`,
            description: '',
            hours: 1,
            hourlyRate: 0,
            lineTotal: 0,
            notes: '',
          };

          if (isMounted) {
            setFormData(newFormData);
            setMaterials([blankMaterial]);
            setLabour([blankLabour]);
            initialFormStateRef.current = { ...newFormData };
            initialMaterialsRef.current = [blankMaterial];
            initialLabourRef.current = [blankLabour];
            initialClientRef.current = null;
          }
        } catch (err) {
          console.error('Error initializing new estimate:', err);
          if (isMounted) {
            const timestamp = Date.now().toString().slice(-6);
            const newFormData = {
              clientId: '',
              estimateNum: `EST-${timestamp}`,
              reference: '',
              dateCreated: new Date().toISOString().split('T')[0],
              dateIssued: '',
              expiryDate: '',
              vatRate: 20,
              notes: '',
              terms: '',
              privateNotes: '',
            };
            setFormData(newFormData);
            initialFormStateRef.current = newFormData;
            initialMaterialsRef.current = [];
            initialLabourRef.current = [];
            initialClientRef.current = null;
          }
        }
      };

      initializeNewEstimate();
    }

    // Cleanup function to clear timeouts
    return () => {
      isMounted = false;
      fetchingRef.current = false;
      Object.keys(autocompleteTimeoutRef.current).forEach(key => {
        if (autocompleteTimeoutRef.current[key]) {
          clearTimeout(autocompleteTimeoutRef.current[key]);
        }
      });
    };
  }, [id, isEditMode, fetchEstimate]);

  const handleClientSelect = (client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, clientId: client.id }));
    setShowClientPicker(false);
    // Reset search state
    setClientSearchQuery('');
    setClientSearchResults([]);
    setShowClientSearchDropdown(false);
    setClientSearchSelectedIndex(-1);
  };

  // Client search function (only triggers after 3+ characters)
  const searchClients = async (query) => {
    if (!query || query.trim().length < 3) {
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
      return;
    }

    try {
      setClientSearchLoading(true);
      const response = await clientsAPI.getAll({
        search: query.trim(),
        page: 1,
        limit: 20,
      });
      
      setClientSearchResults(response.clients || []);
      setShowClientSearchDropdown(true);
      setClientSearchSelectedIndex(-1);
    } catch (err) {
      console.error('Client search error:', err);
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
    } finally {
      setClientSearchLoading(false);
    }
  };

  // Handle client search input change
  const handleClientSearchChange = (value) => {
    setClientSearchQuery(value);

    // Clear previous timeout
    if (clientSearchTimeoutRef.current) {
      clearTimeout(clientSearchTimeoutRef.current);
    }

    // Only search if 3+ characters
    if (value.trim().length >= 3) {
      // Debounce search
      clientSearchTimeoutRef.current = setTimeout(() => {
        searchClients(value);
      }, 300);
    } else {
      setClientSearchResults([]);
      setShowClientSearchDropdown(false);
      setClientSearchSelectedIndex(-1);
    }
  };

  // Handle client selection from dropdown
  const handleClientSearchSelect = (client) => {
    handleClientSelect(client);
  };

  // Handle keyboard navigation for client search
  const handleClientSearchKeyDown = (e) => {
    if (!showClientSearchDropdown || clientSearchResults.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setClientSearchSelectedIndex(prev => 
        Math.min(prev + 1, clientSearchResults.length - 1)
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setClientSearchSelectedIndex(prev => Math.max(prev - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = clientSearchResults[clientSearchSelectedIndex];
      if (selected) {
        handleClientSearchSelect(selected);
      }
    } else if (e.key === 'Escape') {
      setShowClientSearchDropdown(false);
      setClientSearchSelectedIndex(-1);
    }
  };

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        clientSearchContainerRef.current &&
        !clientSearchContainerRef.current.contains(event.target)
      ) {
        setShowClientSearchDropdown(false);
      }
    };

    if (showClientSearchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showClientSearchDropdown]);

  // Cleanup client search timeout on unmount
  useEffect(() => {
    return () => {
      if (clientSearchTimeoutRef.current) {
        clearTimeout(clientSearchTimeoutRef.current);
      }
    };
  }, []);

  const handleAddMaterial = () => {
    setMaterials([...materials, {
      id: `temp-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      partNumber: '',
      notes: '',
    }]);
  };

  const handleInsertMaterial = (index) => {
    const newMaterials = [...materials];
    newMaterials.splice(index + 1, 0, {
      id: `temp-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      partNumber: '',
      notes: '',
    });
    setMaterials(newMaterials);
  };

  const handleUpdateMaterial = (index, field, value) => {
    const updated = [...materials];
    updated[index] = { ...updated[index], [field]: value };
    
    // Recalculate line total
    if (field === 'quantity' || field === 'unitPrice') {
      updated[index].lineTotal = (updated[index].quantity || 0) * (updated[index].unitPrice || 0);
    }
    
    setMaterials(updated);

    // Handle autocomplete for description field
    if (field === 'description') {
      handleMaterialAutocomplete(index, value);
    }
  };

  const handleMaterialAutocomplete = (index, query) => {
    // Clear previous timeout
    if (autocompleteTimeoutRef.current[index]) {
      clearTimeout(autocompleteTimeoutRef.current[index]);
    }

    // Hide autocomplete if query is empty
    if (!query || query.trim().length < 2) {
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: { suggestions: [], selectedIndex: -1, show: false }
      }));
      return;
    }

    // Debounce search
    autocompleteTimeoutRef.current[index] = setTimeout(async () => {
      try {
        const response = await purchasesAPI.getMaterialsLookup(query.trim(), 10);
        setMaterialAutocomplete(prev => ({
          ...prev,
          [index]: {
            suggestions: response.materials || [],
            selectedIndex: -1,
            show: true,
          }
        }));
      } catch (err) {
        console.error('Material lookup error:', err);
        setMaterialAutocomplete(prev => ({
          ...prev,
          [index]: { suggestions: [], selectedIndex: -1, show: false }
        }));
      }
    }, 300);
  };

  const handleMaterialSelect = (index, material) => {
    const updated = [...materials];
    const avgRaw = Number(material.avgUnitPrice) || 0;
    const avgUnit = Math.round(avgRaw * 100) / 100;
    const qty = updated[index].quantity || 0;
    updated[index] = {
      ...updated[index],
      description: material.description,
      partNumber: material.partNumber || '',
      unitPrice: avgUnit,
      lineTotal: qty * avgUnit,
    };
    setMaterials(updated);

    // Hide autocomplete
    setMaterialAutocomplete(prev => ({
      ...prev,
      [index]: { suggestions: [], selectedIndex: -1, show: false }
    }));
  };

  const handleMaterialAutocompleteKeyDown = (index, e) => {
    const autocomplete = materialAutocomplete[index];
    if (!autocomplete || !autocomplete.show || autocomplete.suggestions.length === 0) {
      return;
    }

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          selectedIndex: Math.min(
            prev[index].selectedIndex + 1,
            prev[index].suggestions.length - 1
          )
        }
      }));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: {
          ...prev[index],
          selectedIndex: Math.max(prev[index].selectedIndex - 1, -1)
        }
      }));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const selected = autocomplete.suggestions[autocomplete.selectedIndex];
      if (selected) {
        handleMaterialSelect(index, selected);
      }
    } else if (e.key === 'Escape') {
      setMaterialAutocomplete(prev => ({
        ...prev,
        [index]: { suggestions: [], selectedIndex: -1, show: false }
      }));
    }
  };

  // Materials lookup dropdown: portal + fixed position so it is not clipped by overflow-x-auto on the table wrapper
  useLayoutEffect(() => {
    let activeIndex = null;
    for (const key of Object.keys(materialAutocomplete)) {
      const idx = Number(key);
      const ac = materialAutocomplete[idx];
      if (ac?.show && ac.suggestions?.length > 0) {
        activeIndex = idx;
        break;
      }
    }
    if (activeIndex === null) {
      setMaterialSuggestCoords(null);
      return;
    }
    const updateCoords = () => {
      const el = materialDescInputRefs.current[activeIndex];
      if (!el) {
        setMaterialSuggestCoords(null);
        return;
      }
      const r = el.getBoundingClientRect();
      const minW = 480;
      const maxW = Math.max(200, window.innerWidth - r.left - 12);
      const width = Math.min(Math.max(r.width, minW), maxW);
      setMaterialSuggestCoords({
        index: activeIndex,
        top: r.bottom + 4,
        left: r.left,
        width,
      });
    };
    updateCoords();
    window.addEventListener('scroll', updateCoords, true);
    window.addEventListener('resize', updateCoords);
    return () => {
      window.removeEventListener('scroll', updateCoords, true);
      window.removeEventListener('resize', updateCoords);
    };
  }, [materialAutocomplete]);

  const handleRemoveMaterial = (index) => {
    setMaterials(materials.filter((_, i) => i !== index));
    // Clean up autocomplete state for removed material
    setMaterialAutocomplete(prev => {
      const updated = { ...prev };
      delete updated[index];
      // Reindex remaining autocomplete states
      const reindexed = {};
      Object.keys(updated).forEach(key => {
        const keyNum = parseInt(key);
        if (keyNum > index) {
          reindexed[keyNum - 1] = updated[key];
        } else if (keyNum < index) {
          reindexed[keyNum] = updated[key];
        }
      });
      return reindexed;
    });
    // Clear timeout for removed material
    if (autocompleteTimeoutRef.current[index]) {
      clearTimeout(autocompleteTimeoutRef.current[index]);
      delete autocompleteTimeoutRef.current[index];
    }
    const prevRefs = materialDescInputRefs.current;
    const reindexedRefs = {};
    Object.keys(prevRefs).forEach((key) => {
      const keyNum = parseInt(key, 10);
      if (Number.isNaN(keyNum)) return;
      if (keyNum > index) reindexedRefs[keyNum - 1] = prevRefs[key];
      else if (keyNum < index) reindexedRefs[keyNum] = prevRefs[key];
    });
    materialDescInputRefs.current = reindexedRefs;
  };

  const handleAddLabour = () => {
    setLabour([...labour, {
      id: `temp-${Date.now()}`,
      description: '',
      hours: 1,
      hourlyRate: 0,
      notes: '',
    }]);
  };

  const handleInsertLabour = (index) => {
    const newLabour = [...labour];
    newLabour.splice(index + 1, 0, {
      id: `temp-${Date.now()}`,
      description: '',
      hours: 1,
      hourlyRate: 0,
      notes: '',
    });
    setLabour(newLabour);
  };

  const handleUpdateLabour = (index, field, value) => {
    const updated = [...labour];
    updated[index] = { ...updated[index], [field]: value };
    
    // Recalculate line total
    if (field === 'hours' || field === 'hourlyRate') {
      updated[index].lineTotal = (updated[index].hours || 0) * (updated[index].hourlyRate || 0);
    }
    
    setLabour(updated);
  };

  const handleRemoveLabour = (index) => {
    setLabour(labour.filter((_, i) => i !== index));
  };

  const calculateTotals = () => {
    const materialsSubtotal = materials.reduce((sum, m) => sum + (m.lineTotal || 0), 0);
    const labourSubtotal = labour.reduce((sum, l) => sum + (l.lineTotal || 0), 0);
    const subtotal = materialsSubtotal + labourSubtotal;
    const vatAmount = (subtotal * formData.vatRate) / 100;
    const total = subtotal + vatAmount;

    return { materialsSubtotal, labourSubtotal, subtotal, vatAmount, total };
  };

  // Check if form has unsaved changes
  const hasUnsavedChanges = useCallback(() => {
    if (!initialFormStateRef.current) return false;

    // Compare form data
    const current = formData;
    const initial = initialFormStateRef.current;
    
    if (
      current.clientId !== initial.clientId ||
      current.estimateNum !== initial.estimateNum ||
      current.reference !== initial.reference ||
      current.dateCreated !== initial.dateCreated ||
      current.dateIssued !== initial.dateIssued ||
      current.expiryDate !== initial.expiryDate ||
      current.vatRate !== initial.vatRate ||
      current.notes !== initial.notes ||
      current.terms !== initial.terms ||
      current.privateNotes !== initial.privateNotes
    ) {
      return true;
    }

    // Compare client
    if (selectedClient?.id !== initialClientRef.current?.id) {
      return true;
    }

    // Compare materials
    const materialsStr = JSON.stringify(materials.map(m => ({
      description: m.description,
      quantity: parseFloat(m.quantity) || 0,
      unitPrice: parseFloat(m.unitPrice) || 0,
      partNumber: m.partNumber,
      notes: m.notes,
    })));
    const initialMaterialsStr = JSON.stringify(initialMaterialsRef.current);
    if (materialsStr !== initialMaterialsStr) {
      return true;
    }

    // Compare labour
    const labourStr = JSON.stringify(labour.map(l => ({
      description: l.description,
      hours: parseFloat(l.hours) || 0,
      hourlyRate: parseFloat(l.hourlyRate) || 0,
      notes: l.notes,
    })));
    const initialLabourStr = JSON.stringify(initialLabourRef.current);
    if (labourStr !== initialLabourStr) {
      return true;
    }

    return false;
  }, [formData, selectedClient, materials, labour]);

  const handleNavigationAttempt = (callback) => {
    if (hasUnsavedChanges()) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave? Your changes will be lost.'
      );
      if (!confirmed) return false;
    }
    callback();
    return true;
  };

  // Browser beforeunload handler
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Check for unsaved changes inline to avoid dependency issues
      if (!initialFormStateRef.current) return;
      
      const current = formData;
      const initial = initialFormStateRef.current;
      
      const formChanged = (
        current.clientId !== initial.clientId ||
        current.estimateNum !== initial.estimateNum ||
        current.reference !== initial.reference ||
        current.dateCreated !== initial.dateCreated ||
        current.dateIssued !== initial.dateIssued ||
        current.expiryDate !== initial.expiryDate ||
        current.vatRate !== initial.vatRate ||
        current.notes !== initial.notes ||
        current.terms !== initial.terms ||
        current.privateNotes !== initial.privateNotes ||
        selectedClient?.id !== initialClientRef.current?.id
      );
      
      const materialsChanged = JSON.stringify(materials.map(m => ({
        description: m.description,
        quantity: parseFloat(m.quantity) || 0,
        unitPrice: parseFloat(m.unitPrice) || 0,
        partNumber: m.partNumber,
        notes: m.notes,
      }))) !== JSON.stringify(initialMaterialsRef.current);
      
      const labourChanged = JSON.stringify(labour.map(l => ({
        description: l.description,
        hours: parseFloat(l.hours) || 0,
        hourlyRate: parseFloat(l.hourlyRate) || 0,
        notes: l.notes,
      }))) !== JSON.stringify(initialLabourRef.current);
      
      if (formChanged || materialsChanged || labourChanged) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [formData, selectedClient, materials, labour]);

  // Keep ref updated with latest check function
  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges;
  }, [hasUnsavedChanges]);

  // Register unsaved changes check with context
  useEffect(() => {
    registerCheck(() => {
      if (hasUnsavedChangesRef.current) {
        return hasUnsavedChangesRef.current();
      }
      return false;
    });
    return () => {
      unregisterCheck();
    };
  }, [registerCheck, unregisterCheck]);

  const handleReissue = async () => {
    if (!id || reissuing) return;
    const ok = window.confirm(
      'Re-issue this estimate as Sent? Any stored client link response on this estimate will be cleared (recent activity history is kept). If the public link is still valid, the client can accept, decline, or query again.'
    );
    if (!ok) return;
    try {
      setReissuing(true);
      setError('');
      await estimatesAPI.reissue(id);
      await fetchEstimate();
      setNotification({ show: true, message: 'Estimate re-issued as sent.', type: 'success' });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg =
        err.response?.data?.error || err.response?.data?.message || err.message || 'Could not re-issue estimate';
      setError(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setReissuing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Scroll to top immediately when button is clicked to show notifications
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    if (!formData.clientId) {
      setError('Please select a client');
      return;
    }

    if (!formData.estimateNum || !formData.estimateNum.trim()) {
      setError('Estimate number is required');
      return;
    }

    // Don't allow editing converted estimates
    if (estimateStatus === 'converted') {
      setError('Cannot edit a converted estimate');
      return;
    }

    try {
      setSaving(true);
      setError('');

      const estimateData = {
        clientId: formData.clientId,
        estimateNum: formData.estimateNum,
        reference: formData.reference,
        dateCreated: formData.dateCreated,
        dateIssued: formData.dateIssued || null,
        expiryDate: formData.expiryDate || null,
        vatRate: formData.vatRate,
        notes: formData.notes,
        terms: formData.terms,
        privateNotes: formData.privateNotes,
        materials: materials.map(m => ({
          description: m.description,
          quantity: parseFloat(m.quantity) || 0,
          unitPrice: parseFloat(m.unitPrice) || 0,
          partNumber: m.partNumber,
          notes: m.notes,
        })),
        labour: labour.map(l => ({
          description: l.description,
          hours: parseFloat(l.hours) || 0,
          hourlyRate: parseFloat(l.hourlyRate) || 0,
          notes: l.notes,
        })),
      };

      if (isEditMode) {
        await estimatesAPI.update(id, estimateData);
        
        // Update initial state after successful save
        initialFormStateRef.current = { ...formData };
        initialMaterialsRef.current = JSON.parse(JSON.stringify(materials));
        initialLabourRef.current = JSON.parse(JSON.stringify(labour));
        initialClientRef.current = selectedClient;

        // Scroll to top and show success notification
        window.scrollTo({ top: 0, behavior: 'smooth' });
        setSaveSuccess(true);
        setTimeout(() => {
          setSaveSuccess(false);
        }, 5000); // Hide after 5 seconds
      } else {
        const response = await estimatesAPI.create(estimateData);
        // API returns { message: '...', estimate: {...} } or just { id, ... }
        const estimate = response?.estimate || response;
        const estimateId = estimate?.id;
        
        // If creating a new estimate, show success message, wait, then navigate to edit mode
        if (estimateId) {
          // Show success notification immediately
          setSaveSuccess(true);
          
          // Wait 2.5 seconds before navigating to edit mode (slow reload effect)
          setTimeout(async () => {
            try {
              // Update initial state before navigating
              initialFormStateRef.current = { ...formData };
              initialMaterialsRef.current = JSON.parse(JSON.stringify(materials));
              initialLabourRef.current = JSON.parse(JSON.stringify(labour));
              initialClientRef.current = selectedClient;
              
              // Scroll to top immediately before navigation
              window.scrollTo({ top: 0, behavior: 'auto' });
              
              // Navigate to edit mode
              navigate(`/tenant/estimates/${estimateId}`, { replace: true });
              
              // Wait a moment for React Router to complete navigation and DOM to update
              await new Promise(resolve => setTimeout(resolve, 100));
              
              // Scroll to top again after navigation
              window.scrollTo({ top: 0, behavior: 'auto' });
              
              // Fetch the estimate to update initial state with server data
              await fetchEstimate();
              
              // Scroll to top after fetch completes to ensure we're at the top
              window.scrollTo({ top: 0, behavior: 'auto' });
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }, 50);
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'auto' });
              }, 200);
              
              // Keep success message visible for a bit after loading
              setSaveSuccess(true);
              setTimeout(() => {
                setSaveSuccess(false);
              }, 5000); // Hide after 5 seconds
            } catch (fetchErr) {
              console.error('Error fetching estimate after creation:', fetchErr);
              setError('Estimate created but failed to load. Please refresh the page.');
              // Scroll to top on error too
              window.scrollTo({ top: 0, behavior: 'auto' });
            } finally {
              // Set saving to false after navigation completes
              setSaving(false);
              // Final scroll to top
              setTimeout(() => {
                window.scrollTo({ top: 0, behavior: 'auto' });
              }, 100);
            }
          }, 2500); // 2.5 second delay for slow reload effect
        } else {
          // If no estimate ID, something went wrong
          console.error('Estimate creation response missing ID:', response);
          setError('Estimate was created but could not retrieve the estimate ID. Please refresh the page.');
          setSaving(false);
        }
      }
    } catch (err) {
      const errorMessage = err.message || 'Failed to save estimate';
      setError(errorMessage);
      
      // Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      console.error('Save error:', err);
    } finally {
      // For edit mode, set saving to false immediately after update
      // For create mode with successful creation, saving will remain true during the delay
      // and be set to false in the setTimeout callback after navigation
      if (isEditMode) {
        setSaving(false);
      }
    }
  };

  const handlePrint = async () => {
    if (!id) {
      setError('Please save the estimate first');
      return;
    }
    try {
      setError('');
      const htmlDoc = await estimatesAPI.getHtml(id);
      const w = window.open('', '_blank');
      if (!w) return;
      w.document.write(typeof htmlDoc === 'string' ? htmlDoc : '');
      w.document.close();
      setTimeout(() => {
        w.focus();
        w.print();
      }, 300);
    } catch (err) {
      setError(err.message || 'Failed to open print view');
    }
  };

  const handleConvertToInvoice = async () => {
    if (!id) return;
    try {
      setConverting(true);
      setError('');
      const result = await estimatesAPI.convertToInvoice(id, null, null);
      const newInvoiceId = result?.invoiceId ?? result?.invoice?.id;
      if (!newInvoiceId) {
        setError('Convert succeeded but no invoice id was returned');
        return;
      }
      navigate(`/tenant/invoices/${newInvoiceId}/edit`);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to convert estimate to invoice');
      console.error('Convert error:', err);
    } finally {
      setConverting(false);
    }
  };

  const totals = calculateTotals();
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  const getClientDisplayName = (client) => {
    if (!client) return 'Select client';
    
    // Handle both snake_case (from API) and camelCase formats
    const clientType = client.client_type || client.clientType;
    const companyName = client.company_name || client.companyName;
    const firstName = client.first_name || client.firstName;
    const lastName = client.last_name || client.lastName;
    
    if (clientType === 'company') {
      return companyName || 'Unnamed Company';
    }
    
    const individualName = `${firstName || ''} ${lastName || ''}`.trim();
    return individualName || 'Unnamed Individual';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading estimate...</p>
        </div>
      </div>
    );
  }

  const getExpiryWarning = () => {
    if (!formData.expiryDate || estimateStatus === 'converted') return null;
    
    const expiry = new Date(formData.expiryDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { type: 'expired', message: 'This estimate has expired' };
    } else if (daysUntilExpiry <= 7) {
      return { type: 'warning', message: `This estimate expires in ${daysUntilExpiry} day${daysUntilExpiry !== 1 ? 's' : ''}` };
    }
    return null;
  };

  const expiryWarning = getExpiryWarning();

  // Determine if estimate is read-only (converted or expired estimates cannot be edited)
  const isReadOnly = isEditMode && estimateStatus === 'converted';

  return (
    <Layout>
      {/* Notification Toast */}
      {notification.show && (
        <div className="fixed top-4 right-4 z-50 animate-in slide-in-from-top-5">
          <div className={`rounded-lg shadow-lg p-4 max-w-md ${
            notification.type === 'success' 
              ? 'bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800' 
              : 'bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800'
          }`}>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                {notification.type === 'success' ? (
                  <svg className="h-5 w-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
              <div className="ml-3 flex-1">
                <p className={`text-xs font-medium ${
                  notification.type === 'success'
                    ? 'text-green-800 dark:text-green-200'
                    : 'text-red-800 dark:text-red-200'
                }`}>
                  {notification.message}
                </p>
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => setNotification({ show: false, message: '', type: 'success' })}
                  className={`inline-flex rounded-md p-1.5 ${
                    notification.type === 'success'
                      ? 'text-green-500 hover:bg-green-100 dark:hover:bg-green-900/50'
                      : 'text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50'
                  } focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    notification.type === 'success'
                      ? 'focus:ring-green-600'
                      : 'focus:ring-red-600'
                  }`}
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">
                {isEditMode ? 'Edit Estimate' : 'New Estimate'}
              </h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">
                {isEditMode ? 'Update estimate details' : 'Create a new estimate'}
              </p>
            </div>
            <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
              <Button variant="secondary" onClick={() => handleNavigationAttempt(() => navigate('/tenant/estimates'))}>
                Cancel
              </Button>
              {isEditMode && estimateStatus === 'converted' && (
                <Badge variant="neutral" className="px-4 py-2">
                  Converted to Invoice
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form ref={formRef} onSubmit={handleSubmit} className="space-y-6">
          {/* Success Message */}
          {saveSuccess && (
            <div className="bg-[var(--ds-status-success-bg)] border border-[var(--ds-status-success)]/30 rounded-[var(--ds-radius-md)] p-4 flex items-center justify-between">
              <p className="text-body text-[var(--ds-status-success)] font-medium">
                ✓ Estimate {isEditMode ? 'updated' : 'created'} successfully!
              </p>
              <button
                onClick={() => setSaveSuccess(false)}
                className="text-[var(--ds-status-success)] hover:text-[var(--ds-status-success)]"
                aria-label="Dismiss"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {/* Error Message */}
          {error && (
            <div className="bg-[var(--ds-status-danger-bg)] border border-[var(--ds-status-danger)]/30 rounded-[var(--ds-radius-md)] p-4">
              <p className="text-body text-[var(--ds-status-danger)]">{error}</p>
            </div>
          )}

          {isEditMode && clientPortalFeedback && (
            <Card className="border border-sky-300/80 dark:border-sky-800 bg-sky-50/80 dark:bg-sky-950/30">
              <CardContent className="p-4 space-y-2">
                <div className="flex flex-wrap items-center gap-2">
                  <h3 className="text-sm font-semibold text-[var(--ds-text-primary)]">Client link feedback</h3>
                  <Badge variant="warning" className="text-[10px]">
                    {portalFeedbackActionLabel(clientPortalFeedback.action)}
                  </Badge>
                </div>
                {clientPortalFeedback.action === 'rejected' && (
                  <div className="text-xs text-[var(--ds-text-secondary)] space-y-1">
                    {labelPortalRejectTags(clientPortalFeedback.reasonTags).length > 0 && (
                      <p>
                        <span className="font-medium text-[var(--ds-text-primary)]">Reasons: </span>
                        {labelPortalRejectTags(clientPortalFeedback.reasonTags).join(', ')}
                      </p>
                    )}
                    {clientPortalFeedback.comment ? (
                      <p>
                        <span className="font-medium text-[var(--ds-text-primary)]">Comment: </span>
                        {clientPortalFeedback.comment}
                      </p>
                    ) : null}
                  </div>
                )}
                {clientPortalFeedback.action === 'queried' && (
                  <div className="text-xs text-[var(--ds-text-secondary)] space-y-1">
                    <p>
                      <span className="font-medium text-[var(--ds-text-primary)]">Reason: </span>
                      {PORTAL_QUERY_REASON_LABELS[clientPortalFeedback.queryReason] ||
                        clientPortalFeedback.queryReason}
                    </p>
                    {clientPortalFeedback.details ? (
                      <p>
                        <span className="font-medium text-[var(--ds-text-primary)]">Details: </span>
                        {clientPortalFeedback.details}
                      </p>
                    ) : null}
                  </div>
                )}
                {clientPortalFeedback.action === 'accepted' && (
                  <p className="text-xs text-[var(--ds-text-secondary)]">
                    The client accepted this estimate via the public link
                    {clientPortalFeedback.at
                      ? ` (${new Date(clientPortalFeedback.at).toLocaleString('en-GB')})`
                      : ''}
                    .
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {/* Expiry Warning Banner */}
          {isEditMode && expiryWarning && (
            <div className={`rounded-md p-4 border-l-4 ${
              expiryWarning.type === 'expired' 
                ? 'bg-red-50 dark:bg-red-900/20 border-red-400 dark:border-red-600' 
                : 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-400 dark:border-yellow-600'
            }`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  <span className="text-xl">⚠️</span>
                </div>
                <div className="ml-3">
                  <p className={`text-xs font-medium ${
                    expiryWarning.type === 'expired' 
                      ? 'text-red-800 dark:text-red-300' 
                      : 'text-yellow-800 dark:text-yellow-300'
                  }`}>
                    {expiryWarning.message}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <Card>
            <CardContent className="p-6">
            <h2 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-4">Estimate Details</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-[45%_20%_35%] gap-0">
              {/* Left Column: Client Selection */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300">
                    Client <span className="text-[var(--ds-status-danger)]">*</span>
                  </label>
                  <Button
                    variant="ghost"
                    size="sm"
                    type="button"
                    onClick={() => {
                      if (selectedClient) {
                        // "Modify client" behaviour – clear current client so a new one can be chosen
                        setSelectedClient(null);
                        setFormData(prev => ({ ...prev, clientId: '' }));
                        setClientSearchQuery('');
                        if (isEditMode) {
                          setShowClientPicker(false);
                        }
                      } else {
                        // "+ New Client" behaviour – open slide-over
                        setShowNewClientSlideOver(true);
                      }
                    }}
                    disabled={isReadOnly}
                    className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                  >
                    {selectedClient ? 'Modify client' : '+ New Client'}
                  </Button>
                </div>
                <div className="flex flex-col space-y-2">
                  {!selectedClient && (
                    // No client selected: Show searchable input (same for both create and edit modes)
                    <div className="relative" ref={clientSearchContainerRef}>
                      <Input
                        type="text"
                        value={clientSearchQuery}
                        onChange={(e) => handleClientSearchChange(e.target.value)}
                        onKeyDown={handleClientSearchKeyDown}
                        onFocus={() => {
                          if (clientSearchQuery.trim().length >= 3 && clientSearchResults.length > 0) {
                            setShowClientSearchDropdown(true);
                          }
                        }}
                        placeholder="Type at least 3 characters to search for a client..."
                        disabled={isReadOnly}
                        className="h-8"
                      />
                      {/* Client Search Dropdown */}
                      {showClientSearchDropdown && (
                        <div className="absolute z-50 w-full mt-1 bg-[var(--ds-surface-panel)] border border-[var(--ds-border-subtle)] rounded-[var(--ds-radius-md)] shadow-[var(--ds-shadow-lg)] max-h-60 overflow-y-auto">
                          {clientSearchLoading ? (
                            <div className="px-4 py-3 text-center text-body text-[var(--ds-text-muted)]">
                              <div className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-[var(--ds-accent-primary)]"></div>
                              <span className="ml-2">Searching...</span>
                            </div>
                          ) : clientSearchResults.length > 0 ? (
                            clientSearchResults.map((client, index) => {
                              const clientType = client.clientType || client.client_type;
                              const displayName = clientType === 'company' 
                                ? (client.companyName || client.company_name || 'Unnamed Company')
                                : `${client.firstName || client.first_name || ''} ${client.lastName || client.last_name || ''}`.trim() || 'Unnamed Individual';
                              return (
                                <div
                                  key={client.id}
                                  onClick={() => handleClientSearchSelect(client)}
                                  className={`px-4 py-3 cursor-pointer hover:bg-[var(--ds-control-field-bg)] transition-colors duration-180 ${
                                    index === clientSearchSelectedIndex ? 'bg-[var(--ds-control-field-bg)]' : ''
                                  }`}
                                >
                                  <div className="flex items-center">
                                    <div className="flex-shrink-0 h-8 w-8">
                                      <div className="h-8 w-8 rounded-full bg-[var(--ds-accent-primary-bg)] flex items-center justify-center text-[var(--ds-accent-primary)] font-bold text-micro">
                                        {displayName.charAt(0).toUpperCase()}
                                      </div>
                                    </div>
                                    <div className="ml-3 flex-1">
                                      <div className="flex items-center justify-between">
                                        <div>
                                          <div className="text-body font-medium text-[var(--ds-text-primary)]">
                                            {displayName}
                                          </div>
                                          {client.email && (
                                            <div className="text-xs text-gray-500 dark:text-gray-400">{client.email}</div>
                                          )}
                                        </div>
                                        <div>
                                          {clientType === 'company' ? (
                                            <Building2 className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                          ) : (
                                            <User className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          ) : clientSearchQuery.trim().length >= 3 ? (
                            <div className="px-4 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
                              No clients found
                            </div>
                          ) : null}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                
                {/* Client Details (Read-only) - Show when client is selected */}
                {selectedClient && (() => {
                  // Handle both snake_case and camelCase field names
                  const email = selectedClient.email;
                  const phone = selectedClient.phone;
                  const mobile = selectedClient.mobile;
                  const addressLine1 = selectedClient.address_line1 || selectedClient.addressLine1;
                  const addressLine2 = selectedClient.address_line2 || selectedClient.addressLine2;
                  const city = selectedClient.city;
                  const county = selectedClient.county;
                  const postcode = selectedClient.postcode;
                  const clientType = selectedClient.clientType || selectedClient.client_type;
                  const displayName =
                    clientType === 'company'
                      ? (selectedClient.companyName || selectedClient.company_name || 'Unnamed Company')
                      : (`${selectedClient.firstName || selectedClient.first_name || ''} ${selectedClient.lastName || selectedClient.last_name || ''}`.trim() ||
                        'Unnamed Individual');
                  
                  const hasContact = email || phone || mobile;
                  const hasAddress = addressLine1 || addressLine2 || city || county || postcode;
                  
                  if (!hasContact && !hasAddress) return null;
                  
                  return (
                    <div className="mt-3 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                      {/* Address / Client Name */}
                      {hasAddress && (
                        <div className={hasContact ? "mb-3" : ""}>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">{displayName}</h4>
                          <div className="space-y-1 text-xs text-gray-600 dark:text-gray-400">
                            {addressLine1 && (
                              <div className="flex items-start">
                                <span>{addressLine1}</span>
                              </div>
                            )}
                            {addressLine2 && (
                              <div>{addressLine2}</div>
                            )}
                            {(city || county) && (
                              <div>
                                {city && city}
                                {city && county && ', '}
                                {county && county}
                              </div>
                            )}
                            {postcode && (
                              <div>{postcode}</div>
                            )}
                          </div>
                        </div>
                      )}
                      
                      {/* Contact Information */}
                      {hasContact && (
                        <div>
                          <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">Contact</h4>
                          <div className="space-y-1.5 text-xs">
                            {email && (
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <Mail className="h-3.5 w-3.5" />
                                <span>{email}</span>
                              </div>
                            )}
                            {phone && (
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{phone}</span>
                              </div>
                            )}
                            {mobile && (
                              <div className="flex items-center gap-2 text-gray-600 dark:text-gray-400">
                                <Phone className="h-3.5 w-3.5" />
                                <span>{mobile}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              {/* Empty div to push the estimate details fields to the right */}
              <div></div>

              {/* Right Column: Estimate Details Fields */}
              <div className="space-y-4">
                {/* Estimate Number */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Estimate Number <span className="text-[var(--ds-status-danger)]">*</span>
                  </label>
                  <Input
                    type="text"
                    value={formData.estimateNum}
                    onChange={(e) => setFormData(prev => ({ ...prev, estimateNum: e.target.value }))}
                    required
                    disabled={isReadOnly}
                    variant="borderless"
                    className="ml-auto w-2/4"
                  />
                </div>

                {/* Reference */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Reference
                  </label>
                  <Input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData(prev => ({ ...prev, reference: e.target.value }))}
                    disabled={isReadOnly}
                    variant="borderless"
                    className="ml-auto w-2/4"
                  />
                </div>

                {/* Date Created */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Date Created
                  </label>
                  <Input
                    type="date"
                    value={formData.dateCreated}
                    onChange={(e) => setFormData(prev => ({ ...prev, dateCreated: e.target.value }))}
                    disabled={isReadOnly}
                    variant="borderless"
                    className="ml-auto w-2/4"
                  />
                </div>

                {/* Date Issued */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Date Issued
                  </label>
                  <Input
                    type="date"
                    value={formData.dateIssued}
                    onChange={(e) => {
                      const nextIssued = e.target.value;
                      setFormData((prev) => ({
                        ...prev,
                        dateIssued: nextIssued,
                        expiryDate: nextIssued ? addDaysToYyyyMmDd(nextIssued, 7) : '',
                      }));
                    }}
                    disabled={isReadOnly}
                    variant="borderless"
                    className="ml-auto w-2/4"
                  />
                </div>

                {/* Expiry Date */}
                <div className="flex items-end gap-5">
                  <label className="text-meta font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap w-32 text-right">
                    Expiry Date
                  </label>
                  <Input
                    type="date"
                    value={formData.expiryDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, expiryDate: e.target.value }))}
                    disabled={isReadOnly}
                    variant="borderless"
                    className="ml-auto w-2/4"
                  />
                </div>                
              </div>
            </div>
            </CardContent>
          </Card>

          {/* Materials & Labour Section */}
          <Card>
            <CardContent className="p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-h2 font-medium text-[var(--ds-text-primary)]">Line Items</h2>
              <div className="flex gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={handleAddMaterial}
                  disabled={isReadOnly}
                  className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                >
                  + Add Material
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  type="button"
                  onClick={handleAddLabour}
                  disabled={isReadOnly}
                  className="text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                >
                  + Add Labour
                </Button>
              </div>
            </div>

            {/* Materials Subsection */}
            <div className="mb-8">
              <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-4">Materials</h3>
              {materials.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">No materials added yet</p>
              ) : (
                <div className="relative pr-8 pl-8">
                  {/* Plus buttons positioned outside the table border on the left */}
                  <div className="absolute left-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {materials.map((material, index) => (
                      <div
                        key={`plus-${material.id || index}`}
                        className="flex items-center justify-end"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleInsertMaterial(index)}
                          disabled={isReadOnly}
                          className="flex items-center justify-end text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Add Material"
                          style={{ marginLeft: '0px' }}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Description</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Part Number</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Unit Price</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {materials.map((material, index) => (
                        <tr key={material.id || index} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-2 px-4">
                            <input
                              ref={(el) => {
                                if (el) materialDescInputRefs.current[index] = el;
                                else delete materialDescInputRefs.current[index];
                              }}
                              type="text"
                              value={material.description}
                              onChange={(e) => handleUpdateMaterial(index, 'description', e.target.value)}
                              onKeyDown={(e) => handleMaterialAutocompleteKeyDown(index, e)}
                              onBlur={() => {
                                // Delay hiding to allow click events (portal list uses onMouseDown preventDefault)
                                setTimeout(() => {
                                  setMaterialAutocomplete(prev => ({
                                    ...prev,
                                    [index]: { ...prev[index], show: false }
                                  }));
                                }, 200);
                              }}
                              disabled={isReadOnly}
                              className="w-full px-0 py-1 bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              placeholder="Item description"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="text"
                              value={material.partNumber || ''}
                              onChange={(e) => handleUpdateMaterial(index, 'partNumber', e.target.value)}
                              disabled={isReadOnly}
                              className="w-full px-0 py-1 bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              placeholder="—"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="number"
                              step="0.01"
                              value={material.quantity}
                              onChange={(e) => handleUpdateMaterial(index, 'quantity', parseFloat(e.target.value) || 0)}
                              disabled={isReadOnly}
                              className="w-full px-0 py-1 bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="number"
                              step="0.01"
                              value={material.unitPrice}
                              onChange={(e) => handleUpdateMaterial(index, 'unitPrice', parseFloat(e.target.value) || 0)}
                              disabled={isReadOnly}
                              className="w-full px-0 py-1 bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <span className="text-xs text-gray-900 dark:text-gray-100 font-medium text-right block">
                              {formatCurrency(material.lineTotal || 0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {/* X buttons positioned outside the table border */}
                  <div className="absolute right-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {materials.map((material, index) => (
                      <div
                        key={material.id || index}
                        className="flex items-center justify-start"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleRemoveMaterial(index)}
                          disabled={isReadOnly}
                          className="flex items-center justify-start text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove"
                          style={{ marginRight: '0px' }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {typeof document !== 'undefined' &&
              materialSuggestCoords != null &&
              materialAutocomplete[materialSuggestCoords.index]?.show &&
              (materialAutocomplete[materialSuggestCoords.index]?.suggestions?.length ?? 0) > 0 &&
              createPortal(
                <div
                  className="fixed z-[200] bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md shadow-lg max-h-60 overflow-y-auto text-[10px] leading-snug"
                  style={{
                    top: materialSuggestCoords.top,
                    left: materialSuggestCoords.left,
                    width: materialSuggestCoords.width,
                  }}
                  onMouseDown={(e) => e.preventDefault()}
                >
                  {materialAutocomplete[materialSuggestCoords.index].suggestions.map((suggestion, sugIndex) => (
                    <div
                      key={`${suggestion.description}-${suggestion.partNumber || sugIndex}`}
                      role="option"
                      aria-selected={sugIndex === materialAutocomplete[materialSuggestCoords.index].selectedIndex}
                      onClick={() => handleMaterialSelect(materialSuggestCoords.index, suggestion)}
                      className={`px-3 py-1.5 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${
                        sugIndex === materialAutocomplete[materialSuggestCoords.index].selectedIndex
                          ? 'bg-blue-50 dark:bg-blue-900/20'
                          : ''
                      }`}
                      title={`Last purchased: ${suggestion.lastPurchasedDate ? new Date(suggestion.lastPurchasedDate).toLocaleDateString('en-GB') : 'N/A'} | Avg price: £${suggestion.avgUnitPrice.toFixed(2)}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900 dark:text-gray-100 break-words">
                            {suggestion.description}
                          </div>
                          {suggestion.partNumber && (
                            <div className="text-gray-500 dark:text-gray-400 mt-0.5">
                              Part: {suggestion.partNumber}
                            </div>
                          )}
                        </div>
                        <div
                          className="shrink-0 text-gray-500 dark:text-gray-400 tabular-nums"
                          title={`Last purchased: ${suggestion.lastPurchasedDate ? new Date(suggestion.lastPurchasedDate).toLocaleDateString('en-GB') : 'N/A'}`}
                        >
                          £{suggestion.avgUnitPrice.toFixed(2)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>,
                document.body
              )}

            {/* Labour Subsection */}
            <div>
              <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-4">Labour</h3>
              {labour.length === 0 ? (
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center py-4">No labour items added yet</p>
              ) : (
                <div className="relative pr-8 pl-8">
                  {/* Plus buttons positioned outside the table border on the left */}
                  <div className="absolute left-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {labour.map((labourItem, index) => (
                      <div
                        key={`plus-${labourItem.id || index}`}
                        className="flex items-center justify-end"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleInsertLabour(index)}
                          disabled={isReadOnly}
                          className="flex items-center justify-end text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Add Labour"
                          style={{ marginLeft: '0px' }}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Description</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Hours</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Hourly Rate</th>
                        <th className="text-right py-3 px-4 text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {labour.map((labourItem, index) => (
                        <tr key={labourItem.id || index} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/30">
                          <td className="py-2 px-4">
                            <input
                              type="text"
                              value={labourItem.description}
                              onChange={(e) => handleUpdateLabour(index, 'description', e.target.value)}
                              disabled={isReadOnly}
                              className="w-full px-0 py-1 bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
                              placeholder="Work description"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="number"
                              step="0.25"
                              value={labourItem.hours}
                              onChange={(e) => handleUpdateLabour(index, 'hours', parseFloat(e.target.value) || 0)}
                              disabled={isReadOnly}
                              className="w-full px-0 py-1 bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <input
                              type="number"
                              step="0.01"
                              value={labourItem.hourlyRate}
                              onChange={(e) => handleUpdateLabour(index, 'hourlyRate', parseFloat(e.target.value) || 0)}
                              disabled={isReadOnly}
                              className="w-full px-0 py-1 bg-transparent border-none text-xs text-gray-900 dark:text-gray-100 text-right focus:outline-none focus:ring-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            />
                          </td>
                          <td className="py-2 px-4">
                            <span className="text-xs text-gray-900 dark:text-gray-100 font-medium text-right block">
                              {formatCurrency(labourItem.lineTotal || 0)}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                  {/* X buttons positioned outside the table border */}
                  <div className="absolute right-0 flex flex-col z-10" style={{ top: '42px' }}>
                    {labour.map((labourItem, index) => (
                      <div
                        key={labourItem.id || index}
                        className="flex items-center justify-start"
                        style={{ height: '42px' }}
                      >
                        <button
                          type="button"
                          onClick={() => handleRemoveLabour(index)}
                          disabled={isReadOnly}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded p-1 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Remove"
                          style={{ marginRight: '0px' }}
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            </CardContent>
          </Card>

          {/* Additional Information and Estimate Totals - Side by Side */}
          <div className="grid grid-cols-1 lg:grid-cols-[68%_30%] gap-6">
            {/* Notes & Terms Card - Left (68%) */}
            <Card>
            <CardContent className="p-6">
            <h2 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-4">Additional Information</h2>
            
            {/* Tabs */}
            <div className="border-b border-[var(--ds-border-divider)] mb-4">
              <nav className="-mb-px flex">
                <button
                  type="button"
                  onClick={() => setActiveInfoTab('notes')}
                  disabled={isReadOnly}
                  className={`py-3 px-4 border-b-2 font-medium text-body disabled:opacity-50 disabled:cursor-not-allowed ${
                    activeInfoTab === 'notes'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Notes
                </button>
                <button
                  type="button"
                  onClick={() => setActiveInfoTab('terms')}
                  disabled={isReadOnly}
                  className={`py-3 px-4 border-b-2 font-medium text-body disabled:opacity-50 disabled:cursor-not-allowed ${
                    activeInfoTab === 'terms'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Terms & Conditions
                </button>
                <button
                  type="button"
                  onClick={() => setActiveInfoTab('privateNotes')}
                  disabled={isReadOnly}
                  className={`py-3 px-4 border-b-2 font-medium text-body disabled:opacity-50 disabled:cursor-not-allowed ${
                    activeInfoTab === 'privateNotes'
                      ? 'border-[var(--ds-accent-primary)] text-[var(--ds-accent-primary)]'
                      : 'border-transparent text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)] hover:border-[var(--ds-border-subtle)]'
                  }`}
                >
                  Private Notes
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="mt-4">
              {/* Notes Tab */}
              {activeInfoTab === 'notes' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300">
                      Notes
                    </label>
                    {defaultEstimateNotes && (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, notes: defaultEstimateNotes }))}
                        disabled={isReadOnly}
                        className="text-micro text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                      >
                        Load default
                      </Button>
                    )}
                  </div>
                  <MinimalRichTextEditor
                    value={formData.notes}
                    onChange={(html) => setFormData(prev => ({ ...prev, notes: html }))}
                    rows={6}
                    disabled={isReadOnly}
                    placeholder="Additional notes or comments..."
                    editorClassName="text-xs"
                  />
                </div>
              )}

              {/* Terms Tab */}
              {activeInfoTab === 'terms' && (
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-meta font-medium text-gray-700 dark:text-gray-300">
                      Terms & Conditions
                    </label>
                    {defaultEstimateTerms && (
                      <Button
                        variant="ghost"
                        size="sm"
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, terms: defaultEstimateTerms }))}
                        disabled={isReadOnly}
                        className="text-micro text-[var(--ds-accent-primary)] hover:text-[var(--ds-accent-primary-hover)]"
                      >
                        Load default
                      </Button>
                    )}
                  </div>
                  <MinimalRichTextEditor
                    value={formData.terms}
                    onChange={(html) => setFormData(prev => ({ ...prev, terms: html }))}
                    rows={6}
                    disabled={isReadOnly}
                    placeholder="Terms and conditions for this estimate..."
                    editorClassName="text-xs"
                  />
                </div>
              )}

              {/* Private Notes Tab */}
              {activeInfoTab === 'privateNotes' && (
                <div>
                  <label className="block text-meta font-medium text-[var(--ds-text-primary)] mb-1">
                    Private Notes
                  </label>
                  <MinimalRichTextEditor
                    value={formData.privateNotes}
                    onChange={(html) => setFormData(prev => ({ ...prev, privateNotes: html }))}
                    rows={6}
                    disabled={isReadOnly}
                    placeholder="Private notes (not visible to clients)..."
                    editorClassName="text-xs"
                  />
                  <p className="mt-2 text-body text-[var(--ds-text-muted)]">
                    These notes are only visible to you and your team, not to clients.
                  </p>
                </div>
              )}
            </div>
            </CardContent>
          </Card>

            {/* Totals Summary - Right (30%) */}
            <Card>
            <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex justify-between text-body">
                  {/* VAT Rate */}                
                  <span className="text-[var(--ds-text-muted)]">VAT Rate (%):</span>
                    <input
                      type="text"
                      value={formData.vatRate}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Allow empty string, numbers, and one decimal point
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          const numValue = value === '' ? 0 : parseFloat(value);
                          if (!isNaN(numValue) && numValue >= 0 && numValue <= 100) {
                            setFormData(prev => ({ ...prev, vatRate: numValue }));
                          } else if (value === '') {
                            setFormData(prev => ({ ...prev, vatRate: 0 }));
                          }
                        }
                      }}
                      disabled={isReadOnly}
                      className="w-auto px-2 py-1 bg-transparent border-0 focus:outline-none text-[var(--ds-text-primary)] text-right disabled:opacity-50 disabled:cursor-not-allowed"
                    />
                </div>
              </div>

              <h2 className="text-h2 font-medium text-[var(--ds-text-primary)] mb-4">Estimate Totals</h2>
              <div className="space-y-2">
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)]">Materials Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.materialsSubtotal)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)]">Labour Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.labourSubtotal)}</span>
                </div>
                <div className="flex justify-between text-body border-t border-[var(--ds-border-divider)] pt-2">
                  <span className="text-[var(--ds-text-muted)]">Subtotal:</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.subtotal)}</span>
                </div>
                <div className="flex justify-between text-body">
                  <span className="text-[var(--ds-text-muted)]">VAT ({formData.vatRate}%):</span>
                  <span className="font-medium text-[var(--ds-text-primary)] text-xs">{formatCurrency(totals.vatAmount)}</span>
                </div>
                <div className="flex justify-between text-h2 font-bold border-t border-[var(--ds-border-divider)] pt-2">
                  <span className="text-[var(--ds-text-primary)]">Total:</span>
                  <span className="text-[var(--ds-text-primary)]">{formatCurrency(totals.total)}</span>
                </div>
              </div>
            </CardContent>
          </Card>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3">
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => handleNavigationAttempt(() => navigate('/tenant/estimates'))}
            >
              Cancel
            </Button>
            {isEditMode && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => handleNavigationAttempt(() => navigate(`/tenant/estimates/${id}/view`))}
                  disabled={saving}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button variant="outline" size="sm" type="button" onClick={handlePrint} disabled={saving}>
                  <Printer className="w-4 h-4 mr-2" />
                  Print
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={() => handleNavigationAttempt(() => navigate(`/tenant/estimates/${id}/send`))}
                  disabled={saving}
                >
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </Button>
                {['rejected', 'queried', 'accepted'].includes(estimateStatus) && (
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    onClick={handleReissue}
                    disabled={saving || reissuing}
                    className="border-sky-600/40 text-sky-800 dark:text-sky-200"
                  >
                    <RefreshCw className={`w-4 h-4 mr-2 ${reissuing ? 'animate-spin' : ''}`} />
                    {reissuing ? 'Re-issuing…' : 'Re-issue as sent'}
                  </Button>
                )}
              </>
            )}
            {isEditMode && (estimateStatus === 'accepted' || estimateStatus === 'sent') && (
              <Button
                variant="outline"
                size="sm"
                type="button"
                onClick={handleConvertToInvoice}
                disabled={saving || converting}
                className="border-[var(--ds-accent-primary)]/30 text-[var(--ds-accent-primary)] bg-[var(--ds-accent-primary-bg)] hover:bg-[var(--ds-accent-primary-bg)]"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
                {converting ? 'Converting…' : 'Convert to Invoice'}
              </Button>
            )}
            {!isReadOnly && (
              <Button
                variant="primary"
                size="sm"
                type="submit"
                disabled={saving}
              >
                {saving ? 'Saving...' : isEditMode ? 'Update Estimate' : 'Create Estimate'}
              </Button>
            )}
          </div>
        </form>
      </div>

      {/* Client Picker Modal */}
      {showClientPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">Select Client</h2>
              <button
                onClick={() => setShowClientPicker(false)}
                className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <ClientPicker
              onSelect={handleClientSelect}
              onClose={() => setShowClientPicker(false)}
              showModal={false}
            />
          </div>
        </div>
      )}

      {/* New Client Slide-Over */}
      <SlideOver
        open={showNewClientSlideOver}
        onOpenChange={setShowNewClientSlideOver}
        title="Create New Client"
      >
        <NewClientForm
          onSuccess={(newClient) => {
            handleClientSelect(newClient);
            setShowNewClientSlideOver(false);
          }}
          onCancel={() => setShowNewClientSlideOver(false)}
        />
      </SlideOver>
    </Layout>
  );
};

// New Client Form Component
const NewClientForm = ({ onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    client_type: 'individual',
    company_name: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    country: 'GB',
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const newClient = await clientsAPI.create(formData);
      onSuccess(newClient);
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to create client');
      console.error('Create client error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ 
      ...prev, 
      [name]: value 
    }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
          <p className="text-xs text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Client Type Selection */}
      <div>
        <label className="block text-meta font-medium text-[var(--ds-text-secondary)] mb-2">
          Client Type <span className="text-red-500">*</span>
        </label>
        <div className="flex space-x-4">
          <label className="flex items-center">
            <input
              type="radio"
              name="client_type"
              value="individual"
              checked={formData.client_type === 'individual'}
              onChange={handleChange}
              className="mr-2"
            />
            <span className="flex items-center gap-1 text-gray-900 dark:text-gray-100">
              <User className="h-4 w-4" /> Individual
            </span>
          </label>
          <label className="flex items-center">
            <input
              type="radio"
              name="client_type"
              value="company"
              checked={formData.client_type === 'company'}
              onChange={handleChange}
              className="mr-2"
            />
            <span className="flex items-center gap-1 text-gray-900 dark:text-gray-100">
              <Building2 className="h-4 w-4" /> Company
            </span>
          </label>
        </div>
      </div>

      {/* Company Fields */}
      {formData.client_type === 'company' && (
        <div>
          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
            Company Name <span className="text-red-500">*</span>
          </label>
          <Input
            type="text"
            name="company_name"
            value={formData.company_name}
            onChange={handleChange}
            required
            placeholder="Smith Electrical Ltd"
          />
        </div>
      )}

      {/* Individual Fields */}
      {formData.client_type === 'individual' && (
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              required
              placeholder="John"
            />
          </div>
          <div>
            <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <Input
              type="text"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              required
              placeholder="Smith"
            />
          </div>
        </div>
      )}

      {/* Contact Information */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
            Email
          </label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
          />
        </div>
        <div>
          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
            Phone
          </label>
          <Input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            placeholder="01234 567890"
          />
        </div>
      </div>

      {/* Address (Optional) */}
      <div className="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
        <h3 className="text-xs font-medium text-gray-900 dark:text-gray-100">Address (Optional)</h3>
        <div>
          <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
            Address Line 1
          </label>
          <Input
            type="text"
            name="address_line1"
            value={formData.address_line1}
            onChange={handleChange}
            placeholder="123 Main Street"
          />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
              City
            </label>
            <Input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleChange}
              placeholder="London"
            />
          </div>
          <div>
            <label className="block text-meta font-medium text-gray-700 dark:text-gray-300 mb-1">
              Postcode
            </label>
            <Input
              type="text"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              placeholder="SW1A 1AA"
            />
          </div>
        </div>
      </div>

      {/* Form Actions */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-xs font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Creating...' : 'Create Client'}
        </button>
      </div>
    </form>
  );
};

export default EstimateEditor;

